/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {PersonName} from 'spine-web/proto/spine/people/person_name_pb';
import {EmailAddress} from 'spine-web/proto/spine/net/email_address_pb';
import {Url} from 'spine-web/proto/spine/net/url_pb';
// @ts-expect-error
import {PhoneNumber} from '@proto/teamdev/licensing/values_pb';

/**
 * The information about the person or a legal unit who/which evaluates a product.
 */
export default class Evaluator {
  name: PersonName;
  email: EmailAddress;
  kind: EvaluatorKind;

  constructor(params: {name: PersonName; email: EmailAddress; kind: EvaluatorKind}) {
    this.name = params.name;
    this.email = params.email;
    this.kind = params.kind;
  }
}

/**
 * Implementations represent different kinds of evaluation along with information specific to each
 * respective kind.
 */
export abstract class EvaluatorKind {}

/**
 * Academic evaluation kind.
 */
export class Academic extends EvaluatorKind {
  institutionName: string;
  institutionWebsite: Url;
  projectDescription: string;

  constructor(params: {institutionName: string;
                       institutionWebsite: Url;
                       projectDescription: string;}) {
    super();
    this.institutionName = params.institutionName;
    this.institutionWebsite = params.institutionWebsite;
    this.projectDescription = params.projectDescription;
  }
}

/**
 * Open-source evaluation kind.
 */
export class OpenSource extends EvaluatorKind {
  projectName: string;
  repositoryLink: Url;
  openSourceLicense: LicenseType;
  projectDescription: string;

  constructor(params: {projectName: string; repositoryLink: Url;
                       openSourceLicense: LicenseType; projectDescription: string;}) {
    super();
    this.projectName = params.projectName;
    this.repositoryLink = params.repositoryLink;
    this.openSourceLicense = params.openSourceLicense;
    this.projectDescription = params.projectDescription;
  }
}

/**
 * A type of an open-source license.
 */
export enum LicenseType {
  EOSL_UNKNOWN = 0,
  APACHE = 1,
  BSD_BASED = 2,
  CREATIVE_COMMONS = 3,
  LGPL= 4,
  MIT= 5
}

/**
 * Commercial evaluation kind.
 */
export class Commercial extends EvaluatorKind {
  companyName: string;
  companyWebsite: Url;
  position: PositionInCompany;
  phoneNumber: PhoneNumber;
  teamSize: ProjectTeamSize;
  projectDescription: string;

  constructor(params: {companyName: string; companyWebsite: Url; position: PositionInCompany;
                       phoneNumber: PhoneNumber; teamSize: ProjectTeamSize;
                       projectDescription: string;}) {
    super();
    this.companyName = params.companyName;
    this.companyWebsite = params.companyWebsite;
    this.position = params.position;
    this.phoneNumber = params.phoneNumber;
    this.teamSize = params.teamSize;
    this.projectDescription = params.projectDescription;
  }
}

/**
 * A position in a company.
 *
 * May represent exact or approximate responsibilities of the evaluator.
 *
 * This information reflects the way the communication with a certain person should be
 * established, such as technical level, business goals understanding level, etc.
 */
export enum PositionInCompany {
  ECPIC_UNDEFINED = 0,
  TECHNICAL_TEAM_LEAD = 1,
  SENIOR_SOFTWARE_ENGINEER = 2,
  SOFTWARE_ENGINEER = 3,
  ARCHITECT_TECHNICAL = 4,
  ARCHITECT_SOLUTION = 5,
  TECHNICAL_PM = 6,
  PM = 7,
  SENIOR_MANAGEMENT = 8,
  BUSINESS_ANALYST = 9,
  SYSTEMS_ANALYST = 10,
  TESTING = 11,
  OPS = 12,
  PRODUCT_OWNER = 13,
  SCRUM_MASTER = 14,
  AGILE_COACH = 15,
  UX = 16,
  OTHER = 17
}

/**
 * A discreet value representing the project team size.
 */
export enum ProjectTeamSize {
  ECPTS_UNDEFINED = 0,
  ONE_PERSON = 1,
  FROM_2_TO_5_PEOPLE = 2,
  FROM_6_TO_10_PEOPLE = 3,
  MORE_THAN_10_PEOPLE = 4
}
